// BEAM UI primitive — ListRow.
// Flat surface row: surface bg + default border, NO shadow (lists stay flat for
// sunlight legibility). Title + optional subtitle + optional right/left slots.
import React from 'react';
import { Pressable, StyleSheet, View, type StyleProp, type ViewStyle } from 'react-native';
import { theme } from '../../theme';
import { Text } from './Text';

type ListRowProps = {
  title: string;
  subtitle?: string;
  /** Right-hand slot — a Badge, chevron, amount, etc. */
  right?: React.ReactNode;
  /** Left-hand slot — an icon or the brand mark. */
  left?: React.ReactNode;
  onPress?: () => void;
  disabled?: boolean;
  style?: StyleProp<ViewStyle>;
  /** Overrides the default (title-derived) screen-reader label. */
  accessibilityLabel?: string;
  /** Stable hook for e2e/Maestro wiring flows. */
  testID?: string;
};

export function ListRow({ title, subtitle, right, left, onPress, disabled, style, accessibilityLabel, testID }: ListRowProps) {
  const body = (
    <View style={styles.inner}>
      {left ? <View style={styles.left}>{left}</View> : null}
      <View style={styles.textCol}>
        <Text variant="bodyStrong" numberOfLines={1}>
          {title}
        </Text>
        {subtitle ? (
          <Text variant="small" color="secondary" numberOfLines={1}>
            {subtitle}
          </Text>
        ) : null}
      </View>
      {right ? <View style={styles.right}>{right}</View> : null}
    </View>
  );

  if (!onPress) {
    return <View testID={testID} style={[styles.row, style]}>{body}</View>;
  }
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      testID={testID}
      onPress={onPress}
      disabled={disabled}
      style={({ pressed }) => [styles.row, pressed && styles.pressed, disabled && styles.disabled, style]}
    >
      {body}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  row: {
    minHeight: theme.layout.minTapTarget + theme.spacing.md,
    backgroundColor: theme.color.bg.surface,
    borderRadius: theme.radius.md,
    borderWidth: theme.layout.hairline,
    borderColor: theme.color.border.default,
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.md,
    justifyContent: 'center',
  },
  inner: { flexDirection: 'row', alignItems: 'center', gap: theme.spacing.md },
  left: { justifyContent: 'center' },
  textCol: { flex: 1, gap: 2 },
  right: { marginLeft: theme.spacing.sm },
  pressed: { backgroundColor: theme.color.bg.subtle },
  disabled: { opacity: 0.5 },
});
